import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import moment from 'moment-timezone';

import { getIanaRegion } from '@utils/timezone-helper';
import { TimezoneSharedService } from '@services/timezone/timezone-shared.service';
import { DEFAULT_TIMEZONE } from '@properties/timezone.properties';

/**
 * Renders a UTC date/time in the currently selected display timezone, with a
 * daylight-saving-aware abbreviation derived from the record date.
 *
 * Self-sufficient (AsyncPipe-style): it reads the selected timezone from
 * {@link TimezoneSharedService} and re-renders on change by itself, so consumers
 * just write `{{ date | srTimezoneDate }}` — no service injection, subscription
 * or argument required. An optional zone override may still be passed.
 *
 * Impure (must react to an external timezone change with no input change), so
 * results are memoised per `date|zone` to avoid re-parsing on every CD cycle.
 *
 * @param utcDate - UTC date/time value from the backend (ISO string or Date).
 * @param overrideZone - optional timezone title (e.g. "CST(UTC -6)") to force a zone.
 * @returns e.g. "06/10/2026 14:44 (CDT)", or '' when the input is empty/invalid.
 */
@Pipe({
  name: 'srTimezoneDate',
  standalone: true,
  pure: false,
})
export class SrTimezoneDatePipe implements PipeTransform, OnDestroy {
  private static readonly FALLBACK_FORMATS = ['MM/DD/YYYY HH:mm', 'MM/DD/YYYY HH:mm:ss'];

  private readonly timezoneService = inject(TimezoneSharedService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private currentZone: string = this.timezoneService.selectedTimezone?.title ?? DEFAULT_TIMEZONE.title;

  private readonly subscription: Subscription;

  /** Memoisation: last computed key and its result (impure pipe runs every CD cycle). */
  private lastKey: string | null = null;
  private lastValue = '';

  constructor() {
    // Use the event's timezone (not selectedTimezone): the header emits the change
    // before assigning selectedTimezone, so reading it here would be stale.
    this.subscription = this.timezoneService.timezoneChange.subscribe((event) => {
      this.currentZone = event.timezone?.title ?? this.currentZone;
      this.lastKey = null;
      this.changeDetectorRef.markForCheck();
    });
  }

  transform(utcDate: Date | string | null | undefined, overrideZone?: string): string {
    if (utcDate == null || utcDate === '') {
      return '';
    }

    const zone = overrideZone ?? this.currentZone;
    const key = `${utcDate instanceof Date ? utcDate.getTime() : utcDate}|${zone}`;

    if (key === this.lastKey) {
      return this.lastValue;
    }

    this.lastKey = key;
    this.lastValue = this.format(utcDate, zone);

    return this.lastValue;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private format(utcDate: Date | string, zone: string): string {
    try {
      const parsed = this.parseUtc(utcDate);

      if (!parsed.isValid()) {
        return '';
      }

      const m = parsed.tz(getIanaRegion(zone));

      return `${m.format('MM/DD/YYYY HH:mm')} (${m.format('z')})`;
    } catch (error) {
      return '';
    }
  }

  /**
   * Parses a backend value as UTC. Dates and ISO 8601 strings (the backend format)
   * are handled natively by moment.utc; the explicit US-style formats are a fallback
   * for legacy values.
   */
  private parseUtc(utcDate: Date | string): moment.Moment {
    if (utcDate instanceof Date) {
      return moment.utc(utcDate);
    }

    const nativeParsed = moment.utc(utcDate);

    return nativeParsed.isValid() ? nativeParsed : moment.utc(utcDate, SrTimezoneDatePipe.FALLBACK_FORMATS);
  }
}
